import { useState } from "react";
import {
  Button,
  FormField,
  SubmitButton,
  Textarea,
  useFocusTrap,
  useOverlayDismiss,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { portalPackagesApi } from "../api/packages";
import type { ApprovalChainPortalDto, PortalItemDto } from "../api/types";
import { chainDoneSentence } from "../lib/itemStatus";
import { RESPONSE_NOTE_MAX_LENGTH, responseNoteProblem } from "./ResponseStep";

// Approval chains — the pieces every chain item surface shares (the in-house
// endorse/sign surface and the DocuSign stage): the stage strip, the "your
// step is done" card with the fully signed download, and the Return dialog.
// Everything here reads the caller-relative chain view the server sends
// (`PortalItemDto.approvalChain`, ruling R-C); nothing is re-derived.

/** What the Return dialog tells the party will happen. It must stay true of
 *  the server: the comment goes to Phoenix, the chain stops (Returned), and
 *  staff's revision restarts it at stage 1 on a new document version. */
export const CHAIN_RETURN_CONFIRMATION =
  "Your comments go to Phoenix. The document stops here until it is revised; a revised version restarts from the first party.";

/** The ordered stage summary: role, party and the server's status label. */
export function ApprovalChainProgress({ chain }: { chain: ApprovalChainPortalDto }) {
  if (chain.stages.length === 0) return null;
  const stages = [...chain.stages].sort((a, b) => a.order - b.order);
  return (
    <section
      className="flex flex-col gap-space-2 p-space-4 border border-border-light rounded-md bg-white-pure"
      aria-label="Approval progress"
      data-testid="chain-progress"
    >
      <h2 className="text-body font-display font-semibold text-onyx m-0">Approval progress</h2>
      <ol className="flex flex-col gap-space-2 list-none p-0 m-0">
        {stages.map((s, idx) => (
          <li
            // Co-signers share an order, so the index keeps the key unique.
            key={`${s.order}-${idx}`}
            className="flex flex-wrap items-baseline justify-between gap-x-space-3 gap-y-space-1 border-t border-border-light pt-space-2 first:border-t-0 first:pt-0"
            data-testid="chain-stage"
            data-stage-status={s.status}
          >
            <span className="min-w-0 text-small text-onyx">
              <span className="font-medium">{s.roleLabel}</span>
              <span className="text-gray-mid"> · {s.partyName}</span>
            </span>
            <span className="text-caption text-gray-mid">{s.statusLabel}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** The caller has done their step. Once every party has signed, the fully
 *  signed PDF is downloadable (the server serves it to every chain party). */
export function ChainDoneCard({ item }: { item: PortalItemDto }) {
  const chain = item.approvalChain;
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  if (!chain) return null;

  async function download() {
    setError(null);
    setOpening(true);
    try {
      const dl = await portalPackagesApi.getItemDocument(item.id);
      window.open(dl.sasUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't open that document.");
    } finally {
      setOpening(false);
    }
  }

  return (
    <div
      className="flex flex-col gap-space-3 p-space-4 border border-border-light rounded-md bg-white-pure"
      data-testid="chain-done"
    >
      <h2 className="text-body font-display font-semibold text-onyx m-0">
        {chain.myAction === "Endorse" ? "Endorsement recorded" : "Signature recorded"}
      </h2>
      <p className="text-small text-onyx m-0">{chainDoneSentence(chain.myAction)}</p>
      {!chain.isCompleted && (
        <p className="text-small text-gray-mid m-0" data-testid="chain-done-status">
          {chain.statusLabel}
        </p>
      )}
      {chain.isCompleted && (
        <div>
          <Button
            variant="secondary"
            onClick={() => void download()}
            disabled={opening}
            data-testid="download-signed-document"
          >
            Download signed document
          </Button>
        </div>
      )}
      {error && (
        <p className="text-small text-status-error m-0" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** Return the document to Phoenix with a required comment. */
export function ChainReturnDialog({
  itemId,
  onClose,
  onReturned,
}: {
  itemId: string;
  onClose: () => void;
  onReturned: (next: PortalItemDto) => void;
}) {
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);
  const trimmed = comment.trim();
  const problem = responseNoteProblem(comment);
  const tooLong = trimmed.length > RESPONSE_NOTE_MAX_LENGTH;

  const submit = useSubmittingMutation(
    async () => {
      const next = await portalPackagesApi.returnApprovalChain(itemId, trimmed);
      onReturned(next);
    },
    {
      onError: (err) =>
        setError(err instanceof Error ? err.message : "Couldn't return the document. Try again."),
    },
  );

  const close = () => {
    if (!submit.pending) onClose();
  };
  useOverlayDismiss(true, close);
  const trap = useFocusTrap<HTMLDivElement>({ active: true });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-onyx/50 p-space-4"
      data-testid="chain-return-overlay"
    >
      <div
        ref={trap.containerRef}
        onKeyDown={trap.onKeyDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby="chain-return-title"
        className="max-w-lg w-full rounded-md bg-white-pure p-space-5 shadow-lg flex flex-col gap-space-3"
        data-testid="chain-return-dialog"
      >
        <h2 id="chain-return-title" className="text-lead font-display text-onyx m-0">
          Return with comments
        </h2>
        <p className="text-small text-onyx m-0" data-testid="chain-return-confirmation">
          {CHAIN_RETURN_CONFIRMATION}
        </p>
        <FormField
          label="Your comments"
          required
          error={tooLong && problem ? problem : undefined}
          helperText={`${trimmed.length.toLocaleString("en-US")} of 4,000 characters.`}
        >
          <Textarea
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              setError(null);
            }}
            disabled={submit.pending}
            invalid={tooLong}
            rows={4}
            data-testid="chain-return-comment"
          />
        </FormField>
        <div className="flex flex-wrap justify-end gap-space-2">
          <Button variant="secondary" onClick={close} disabled={submit.pending}>
            Cancel
          </Button>
          <SubmitButton
            onClick={() => {
              if (problem) return;
              setError(null);
              void submit.submit();
            }}
            pending={submit.pending}
            pendingLabel="Returning…"
            disabled={problem !== null}
            data-testid="chain-return-submit"
          >
            Return to Phoenix
          </SubmitButton>
        </div>
        {error && (
          <p className="text-small text-status-error m-0" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
